import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Container,
  Fab,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import RedirectBackButton from "components/RedirectBackButton";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  IListResponseOfMenu,
  ListRequestOfMenuFilter,
  ListResponseOfMenu,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";

interface IListResponseOfMenuWithId extends IListResponseOfMenu {
  id: string;
}

const MenuList = () => {
  const queryClient = useQueryClient();
  const { formatMessage: t } = useIntl();
  const {
    menuService,
    menuCreateUrl,
    menuDetailsUrl,
    dynamicMenuUrl,
  } = useGlobalContext();

  const { organizationId } = contextStore.useState();

  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchTerm = persistanceStore.useState((s) => s.menuSearchTerm);
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  useListTitle("menusLabel");

  const { isError, data, error, isFetching } = useQuery<ListResponseOfMenu>(
    [
      "menus",
      organizationId,
      page,
      rowsPerPage,
      orderBy,
      order,
      debouncedSearchTerm,
    ],
    () =>
      menuService.getMenus(
        ListRequestOfMenuFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: {
            searchText: debouncedSearchTerm,
          },
        })
      ),
    { keepPreviousData: true }
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, organizationId]);

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "description",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "descriptionLabel" }),
    },
    {
      accessor: "menuType",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "menuTypeLabel" }),
    },
    {
      accessor: "organizationName",
      customSortAccessor: "organization.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "ownerOrganization" }),
    },
  ];

  const menus = data?.data as IListResponseOfMenuWithId[];

  const onClick = (id: string) => {
    queryClient.invalidateQueries(["menuItems", id]);
    navigate(menuDetailsUrl(id));
  };

  return (
    <Container>
      <RedirectBackButton
        redirectRoute={dynamicMenuUrl()}
        labelKey="backToDynamicMenu"
      />
      <Box mb={3} textAlign="center">
        <Link to={menuCreateUrl()} style={{ textDecoration: "none" }}>
          <Fab aria-label="add" color="primary" data-cy="addButton">
            <AddIcon />
          </Fab>
        </Link>
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          value={searchTerm ?? ""}
          placeholder={t({ id: "searchPlaceholder" })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() =>
                    persistanceStore.update((s) => {
                      s.menuSearchTerm = "";
                    })
                  }
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={(e) => {
            persistanceStore.update((s) => {
              s.menuSearchTerm = e.target.value;
            });
          }}
          data-cy="searchText"
        />
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: menus ?? [],
          loading: isFetching,
          dense: true,
          title: t({ id: "menusLabel" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => menuDetailsUrl(id)),
          ],
        }}
      />
    </Container>
  );
};

export default MenuList;
